import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ShowcaseFeedbackForm } from "./ShowcaseFeedbackForm";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface ShowcaseFeedbackButtonProps {
  organizationId: string;
  organizationName: string;
}

export function ShowcaseFeedbackButton({ organizationId, organizationName }: ShowcaseFeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleSuccess = () => {
    toast({
      title: "Thank you for your feedback!",
      description: "Your feedback has been submitted successfully.",
    });
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleError = (error: Error) => {
    toast({
      title: "Error submitting feedback",
      description: error.message || "Please try again later.",
      variant: "destructive",
    });
  };

  const formContent = (
    <ShowcaseFeedbackForm
      organizationId={organizationId}
      organizationName={organizationName}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button
            size="lg"
            className="fixed bottom-6 right-6 h-14 rounded-full transition-all duration-300 gap-2 z-50 animate-subtle-glow hover:animate-none hover:shadow-xl"
          >
            <MessageSquare className="h-5 w-5" />
            <span className="font-medium">Share Feedback</span>
          </Button>
        </DrawerTrigger>
        <DrawerContent className="showcase-dialog max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>Share Your Feedback</DrawerTitle>
            <DrawerDescription>
              Help us improve by sharing your thoughts about Snapshot Devices
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">
            {formContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          className="fixed bottom-6 right-6 h-14 rounded-full transition-all duration-300 gap-2 z-50 animate-subtle-glow hover:animate-none hover:shadow-xl"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="font-medium">Share Feedback</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="showcase-dialog max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Your Feedback</DialogTitle>
          <DialogDescription>
            Help us improve by sharing your thoughts about Snapshot Devices
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
